import { useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { format } from "date-fns"
import {
	IconArrowRightOutline18 as ArrowRight,
	IconPlusOutline18 as Plus,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import { normalizeEmail } from "@productlane/api/src/lib/contact"
import { generateId } from "@productlane/lib/src/browser"
import { primaryAddress } from "@productlane/lib/src/email-settings"
import {
	Badge,
	Button,
	Dialog,
	Empty,
	Form,
	FormInput,
	IconButton,
	Loader,
	OptionCards,
	SubmitButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ForwardedEmailHandling } from "./modes"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import {
	EMAIL_MAXIMUM_LENGTH,
	EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE,
} from "@/constants"
import { isQuerySettled } from "@/lib/query-settled"
import { useSession } from "@/lib/session"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "../../settings-header"
import { SettingsContainer } from "../../settings-switches/settings-container"
import {
	FORWARDED_MODE_OPTIONS,
	forwardedCaseContact,
	forwardedCases,
} from "./modes"

const CreateForwardedExceptionFormSchema = z.object({
	email: z
		.string()
		.min(1, "Email is required")
		.email("Please enter a valid email address")
		.max(EMAIL_MAXIMUM_LENGTH, EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE),
})

export const ForwardedEmailHandlingSettingsPage = () => {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const { session } = useSession()
	const emailSettings = primaryAddress(workspace?.emailSettings)
	const mode: ForwardedEmailHandling =
		emailSettings?.forwardedEmailHandling ?? "AUTO"
	const exceptionsActive = mode === "ORIGINAL_SENDER"

	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
	const [deletingId, setDeletingId] = useState<string | null>(null)

	const [exceptions, exceptionsResult] = useQuery(
		queries.forwardedEmailIgnoreSenders(),
	)

	const form = useZodForm({
		schema: CreateForwardedExceptionFormSchema,
		defaultValues: { email: "" },
	})

	// The page offers one choice, so it has to land on every inbox; inbound mail
	// reads the setting off whichever address received it.
	const handleModeChange = async (value: string) => {
		const addresses = workspace?.emailSettings ?? []
		if (addresses.length === 0) return
		const next = value as ForwardedEmailHandling
		if (next === mode) return
		const results = await Promise.all(
			addresses.map(
				(address) =>
					zero.mutate(
						mutators.emailSettings.update({
							id: address.id,
							forwardedEmailHandling: next,
						}),
					).client,
			),
		)
		const failed = results.find((result) => result.type !== "success")
		if (failed) {
			toast.error({
				title: "Failed to update setting",
				description: failed.error?.message ?? "Unknown error",
			})
		}
	}

	const handleCreate = async (
		values: z.infer<typeof CreateForwardedExceptionFormSchema>,
	) => {
		const normalizedEmail = normalizeEmail(values.email)

		const result = await zero.mutate(
			mutators.forwardedEmailIgnoreSenders.create({
				id: generateId(),
				email: normalizedEmail,
			}),
		).client

		if (result.type === "success") {
			toast.success({ title: "Exception added" })
			setIsCreateDialogOpen(false)
			form.reset()
		} else {
			toast.error({
				title: "Failed to add exception",
				description: result.error?.message ?? "Unknown error",
			})
		}
	}

	const handleDelete = async (id: string) => {
		setDeletingId(id)
		const result = await zero.mutate(
			mutators.forwardedEmailIgnoreSenders.delete({
				id,
			}),
		).client

		if (result.type === "success") {
			toast.success({ title: "Exception removed" })
		} else {
			toast.error({
				title: "Failed to remove exception",
				description: result.error?.message ?? "Unknown error",
			})
		}
		setDeletingId(null)
	}

	return (
		<>
			<MetaInfo title="Forwarded emails" />

			<SettingsHeader
				title="Forwarded emails"
				subtitle="Who becomes the contact when someone forwards a message into your inbox."
			/>

			<SettingsContainer className="mb-8">
				<OptionCards
					name="forwardedEmailHandling"
					value={mode}
					options={FORWARDED_MODE_OPTIONS}
					onValueChange={(value) => {
						void handleModeChange(value)
					}}
					className="grid-cols-1 sm:grid-cols-3"
				/>

				<div className="pt-4">
					<div className="mb-2.5 text-xs font-medium text-secondary">
						Examples
					</div>
					<div className="flex flex-col gap-2">
						{forwardedCases(session?.email).map((forwardedCase) => (
							<div
								key={forwardedCase.key}
								className="rounded-lg bg-gray-50 px-3 py-2.5 ring-1 ring-black/5 dark:bg-gray-800/60 dark:ring-white/10"
							>
								<div className="text-xs leading-relaxed text-secondary">
									<span className="break-all font-medium text-primary">
										{forwardedCase.forwarder}
									</span>{" "}
									forwards an email from{" "}
									<span className="break-all font-medium text-primary">
										{forwardedCase.originalSender}
									</span>
								</div>
								<div className="mt-1.5 flex items-center gap-1.5">
									<span className="text-xs text-tertiary">Contact</span>
									<ArrowRight className="h-3 w-3 flex-none text-tertiary" />
									<Badge variant="light-gray" className="max-w-full">
										<span className="truncate">
											{forwardedCaseContact(mode, forwardedCase)}
										</span>
									</Badge>
								</div>
							</div>
						))}
					</div>
				</div>
			</SettingsContainer>

			{exceptionsActive && (
				<div>
					<div className="mb-2 flex items-center justify-between">
						<div className="font-semibold text-primary">Exceptions</div>
						<Button
							onClick={() => {
								setIsCreateDialogOpen(true)
							}}
							variant="outline"
							Icon={Plus}
						>
							Add
						</Button>
					</div>
					<p className="mb-4 text-xs text-secondary">
						When one of these writes the message inside a forward, the forwarder
						becomes the contact instead.
					</p>

					<SettingsContainer>
						{!isQuerySettled(exceptionsResult) ? (
							<div className="flex items-center justify-center py-8">
								<Loader />
							</div>
						) : exceptions.length === 0 ? (
							<Empty text="No exceptions" className="py-8" />
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Email</TableHead>
										<TableHead>Added</TableHead>
										<TableHead className="w-[4rem] text-right"></TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{exceptions.map((entry) => (
										<TableRow key={entry.id}>
											<TableCell className="max-w-80 truncate font-medium">
												{entry.email}
											</TableCell>
											<TableCell>
												<span className="inline-flex">
													{format(new Date(entry.createdAt), "dd.MM.yyyy")}
												</span>
											</TableCell>
											<TableCell className="w-[4rem] text-right">
												<IconButton
													Icon={Trash}
													onClick={() => {
														void handleDelete(entry.id)
													}}
													disabled={deletingId === entry.id}
												/>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</SettingsContainer>
				</div>
			)}

			<Dialog
				isOpen={isCreateDialogOpen}
				onOpenChange={(open) => {
					setIsCreateDialogOpen(open)
					if (!open) {
						setTimeout(() => {
							form.reset()
						}, 200)
					}
				}}
				title="Add an exception"
				description="The thread will be attributed to the forwarder when this address appears as the original sender of a forwarded email."
			>
				<div className="mb-2 mt-4 text-sm">Email address</div>
				<Form form={form} onSubmit={handleCreate} className="space-y-4">
					<FormInput name="email" placeholder="info@example.com" />
					<div className="flex justify-end">
						<SubmitButton className="w-28">Add</SubmitButton>
					</div>
				</Form>
			</Dialog>
		</>
	)
}
